import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Phone, UserPlus } from "lucide-react";
import { DispatchApi, BookingApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { useRealtime } from "../../realtime/RealtimeProvider";
import { EmptyState, PageHeader, PriorityBadge, SimBadge, Spinner, StatusBadge } from "../../components/common";
import { MapView, MapMarker } from "../../maps/MapView";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { ago, kmBetween, phone, timeOnly } from "../../lib/format";

const OVERRIDE_NEXT: Record<string, string> = { accepted: "en_route", en_route: "at_pickup", at_pickup: "in_transit", in_transit: "completed" };

function AssignDialog({ booking, responders, onClose }: { booking: any; responders: any[]; onClose: () => void }) {
  const qc = useQueryClient();
  const assign = useMutation({
    mutationFn: (rid: string) => DispatchApi.assign(booking.id, rid),
    onSuccess: () => { toast.success("Responder assigned"); qc.invalidateQueries({ queryKey: ["dispatch"] }); onClose(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const list = responders
    .map((r) => ({ ...r, km: kmBetween(r.location, booking.pickup), eligible: r.verification === "verified" && r.status === "active" && r.availability === "available" && !r.active_booking }))
    .sort((a, b) => Number(b.eligible) - Number(a.eligible) || (a.km ?? 999) - (b.km ?? 999));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Assign responder · {booking.code}</DialogTitle>
          <DialogDescription>Only verified, available responders can be assigned. Distances are straight-line.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2">
          {list.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3" data-testid="assign-responder-option">
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm font-semibold">{r.name} <SimBadge show={r.is_simulated} /></p>
                <p className="text-xs text-slate-500">{r.vehicle?.plate || "No vehicle"} · {r.km != null ? `${r.km.toFixed(1)} km` : "location unknown"} · {r.verification !== "verified" ? "unverified" : r.active_booking ? `on ${r.active_booking.code}` : r.availability}</p>
              </div>
              <button className="fp-btn-primary min-h-[40px] px-3 text-xs" disabled={!r.eligible || assign.isPending} onClick={() => assign.mutate(r.id)} data-testid={`assign-responder-${r.id}`}>Assign</button>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}

function QueueCard({ b, selected, onSelect, onAssign }: { b: any; selected: boolean; onSelect: () => void; onAssign: () => void }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["dispatch"] });
  const prio = useMutation({ mutationFn: (p: number) => DispatchApi.priority(b.id, p), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const status = useMutation({ mutationFn: (s: string) => DispatchApi.status(b.id, s), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const cancel = useMutation({ mutationFn: () => BookingApi.cancel(b.id, "Cancelled by dispatch"), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const next = OVERRIDE_NEXT[b.status];
  return (
    <div onClick={onSelect} className={`cursor-pointer rounded-2xl border bg-white p-4 ${selected ? "border-[#036248] ring-2 ring-[#036248]/20" : "border-slate-200"} ${b.type === "emergency" && b.status === "requested" ? "border-l-4 border-l-[#FFC400]" : ""}`} data-testid="dispatch-queue-item">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-sm font-semibold">{b.code}</p>
        <div className="flex items-center gap-1.5"><PriorityBadge p={b.priority} /><StatusBadge status={b.status} /></div>
      </div>
      <p className="mt-2 text-sm font-medium text-slate-900">{b.service === "ptv" ? "PTV" : b.type === "emergency" ? "SOS Ambulance" : "Scheduled"} · {b.condition || "—"}{b.addons?.length ? ` · + ${b.addons.join(", ")}` : ""}</p>
      <p className="truncate text-sm text-slate-600">{b.pickup.address || `${b.pickup.lat.toFixed(4)}, ${b.pickup.lng.toFixed(4)}`}</p>
      <p className="mt-1 flex items-center gap-2 text-xs text-slate-500">
        {b.patient?.name} · <a href={`tel:${b.contact_phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 underline"><Phone size={11} />{phone(b.contact_phone)}</a> · {b.scheduled_at ? `for ${timeOnly(b.scheduled_at)}` : ago(b.created_at)}
      </p>
      {b.responder && <p className="mt-1 flex items-center gap-2 text-xs text-slate-700">Responder: <strong>{b.responder.name}</strong> <SimBadge show={b.responder.is_simulated} /></p>}
      <div className="mt-3 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
        {["requested", "assigned"].includes(b.status) && (
          <button className="fp-btn-primary min-h-[40px] px-3 text-xs" onClick={onAssign} data-testid="dispatch-assign-button"><UserPlus size={14} />{b.status === "assigned" ? "Reassign" : "Assign"}</button>
        )}
        <select aria-label="Priority" className="h-10 rounded-xl border border-slate-300 px-2 text-xs" value={b.priority} onChange={(e) => prio.mutate(Number(e.target.value))} data-testid="dispatch-priority-select">
          {[1, 2, 3, 4].map((p) => <option key={p} value={p}>Priority {p}</option>)}
        </select>
        {b.status === "assigned" && b.responder?.is_simulated && <button className="fp-btn-yellow min-h-[40px] px-3 text-xs" onClick={() => status.mutate("accepted")} data-testid="dispatch-accept-sim">Accept for simulated unit</button>}
        {next && <button className="fp-btn-secondary min-h-[40px] px-3 text-xs" onClick={() => status.mutate(next)} data-testid="dispatch-status-advance">Mark {next.replace(/_/g, " ")}</button>}
        <button className="fp-btn-secondary min-h-[40px] px-3 text-xs text-amber-800" onClick={() => window.confirm(`Cancel ${b.code}?`) && cancel.mutate()} data-testid="dispatch-cancel-button">Cancel</button>
      </div>
    </div>
  );
}

function Alerts({ alerts }: { alerts: any[] }) {
  if (!alerts?.length) return null;
  return (
    <div className="space-y-2" data-testid="dispatch-alerts">
      {alerts.map((a) => (
        <div key={a.id} role="alert" className={`flex gap-2 rounded-xl border p-3 text-sm ${a.level === "critical" ? "border-[#FFC400] bg-[#FFF4CC] text-[#003F32]" : "border-amber-300 bg-amber-50 text-amber-900"}`} data-testid="dispatch-alert-item">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span><strong>{a.title}.</strong> {a.body}</span>
        </div>
      ))}
    </div>
  );
}

function ResponderList({ responders }: { responders: any[] }) {
  return (
    <ul className="space-y-2" data-testid="dispatch-responder-list">
      {responders.map((r) => (
        <li key={r.id} className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="dispatch-responder-item">
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">{r.name}<SimBadge show={r.is_simulated} /></p>
            <p className="text-xs text-slate-500">{r.vehicle?.plate || "No vehicle"}{r.active_booking ? ` · ${r.active_booking.code}` : ""}{r.location?.updated_at ? ` · loc ${ago(r.location.updated_at)}` : " · no location"}</p>
          </div>
          <StatusBadge status={r.verification !== "verified" ? "pending" : r.availability} label={r.verification !== "verified" ? "unverified" : undefined} />
        </li>
      ))}
    </ul>
  );
}

export default function DispatchBoard() {
  const { status, lastSync } = useRealtime();
  const poll = status === "open" ? false : 15000;
  const { data: queue, isLoading } = useQuery({ queryKey: ["dispatch", "queue"], queryFn: DispatchApi.queue, refetchInterval: poll });
  const { data: responders = [] } = useQuery({ queryKey: ["dispatch", "responders"], queryFn: DispatchApi.responders, refetchInterval: poll });
  const { data: alerts = [] } = useQuery({ queryKey: ["dispatch", "alerts"], queryFn: DispatchApi.alerts, refetchInterval: 20000 });
  const [selected, setSelected] = useState<string | null>(null);
  const [assigning, setAssigning] = useState<any>(null);
  const [tab, setTab] = useState<"queue" | "map" | "units">("queue");

  const markers = useMemo<MapMarker[]>(() => [
    ...(queue || []).map((b) => ({ id: b.id, lat: b.pickup.lat, lng: b.pickup.lng, kind: "pickup" as const, label: `${b.code} · ${b.status}`, selected: b.id === selected })),
    ...responders.filter((r) => r.location).map((r) => ({
      id: r.id, lat: r.location.lat, lng: r.location.lng, label: `${r.name}${r.is_simulated ? " (simulated)" : ""} · ${r.availability}`,
      kind: (r.availability === "available" ? "responder" : r.availability === "busy" ? "responder-busy" : "responder-offline") as MapMarker["kind"],
    })),
  ], [queue, responders, selected]);

  if (isLoading) return <Spinner />;
  const waiting = (queue || []).filter((b) => b.status === "requested").length;
  const available = responders.filter((r) => r.availability === "available" && r.verification === "verified").length;
  const tabCls = (t: string) => `flex-1 rounded-lg py-2 text-sm font-semibold ${tab === t ? "bg-white text-[#036248] shadow-sm" : "text-slate-600"}`;

  return (
    <div className="space-y-4">
      <PageHeader title="Live dispatch" subtitle={`Last server sync ${lastSync ? timeOnly(lastSync.toISOString()) : "—"}${status !== "open" ? " · realtime reconnecting, polling every 15 s" : ""}`} />
      <div className="grid grid-cols-3 gap-3">
        <div className="fp-card"><p className="fp-label">Waiting</p><p className="text-2xl font-bold text-amber-600" data-testid="stat-waiting">{waiting}</p></div>
        <div className="fp-card"><p className="fp-label">Active</p><p className="text-2xl font-bold" data-testid="stat-active">{(queue || []).length}</p></div>
        <div className="fp-card"><p className="fp-label">Available units</p><p className="text-2xl font-bold text-[#036248]" data-testid="stat-available">{available}</p></div>
      </div>
      <Alerts alerts={alerts} />
      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 lg:hidden">
        <button className={tabCls("queue")} onClick={() => setTab("queue")} data-testid="dispatch-tab-queue">Queue</button>
        <button className={tabCls("map")} onClick={() => setTab("map")} data-testid="dispatch-tab-map">Map</button>
        <button className={tabCls("units")} onClick={() => setTab("units")} data-testid="dispatch-tab-units">Units</button>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(320px,1fr)_1.4fr_300px]">
        <section className={`space-y-3 ${tab === "queue" ? "" : "hidden lg:block"}`} data-testid="dispatch-queue">
          <h2 className="fp-label hidden lg:block">Queue · priority order</h2>
          {queue?.length ? queue.map((b) => <QueueCard key={b.id} b={b} selected={b.id === selected} onSelect={() => setSelected(b.id)} onAssign={() => setAssigning(b)} />)
            : <EmptyState title="No active requests" testId="dispatch-queue-empty" />}
        </section>
        <section className={`${tab === "map" ? "" : "hidden lg:block"}`}>
          <MapView className="h-[420px] lg:sticky lg:top-24 lg:h-[calc(100vh-260px)]" markers={markers} testId="dispatch-map" />
          <p className="mt-2 text-xs text-slate-500">Red: pickups · Green: available · Yellow: busy · Grey: offline. Simulated units are development data, not real resources.</p>
        </section>
        <section className={`space-y-2 ${tab === "units" ? "" : "hidden lg:block"}`}>
          <h2 className="fp-label hidden lg:block">Responders</h2>
          <ResponderList responders={responders} />
        </section>
      </div>
      {assigning && <AssignDialog booking={assigning} responders={responders} onClose={() => setAssigning(null)} />}
    </div>
  );
}
